/**
 * Sending a body somewhere and watching how far it has got (D-901).
 *
 * <b>`XMLHttpRequest`, because `fetch` cannot report upload progress.</b> There
 * is no event for a request body being sent, so a phone pushing 240 MB over
 * studio wifi is indistinguishable from a phone that has frozen. That is the
 * only reason this is not a `fetch` call like every other request in the app.
 *
 * ⚠ <b>It lives here because there were two of these</b> (A-17). The practice
 * video upload had one (D-669) and the messaging attachment upload grew a
 * second (D-901), and they had already drifted in three places before either
 * was a day old: one handled a zero `total`, the other handled `timeout` and
 * `abort`, and only one of them knew what a 403 from a create-only signed URL
 * actually means. The next caller uses this rather than writing a third.
 *
 * <b>Its only caller is the upload core</b> (`mediaUpload.ts`, D-909), which
 * owns the branch between storage and the app and every sentence above it.
 * This stays the one-request transport beneath it.
 *
 * <b>It resolves on any answer, including a refusal</b>, and rejects only when
 * there was no answer at all. Which statuses are acceptable, and what to say
 * about the ones that are not, belongs to the caller — the two callers word it
 * differently and both are right for where they appear.
 */
export type Sent = { status: number; text: string }

export function send(
  method: string,
  url: string,
  body: XMLHttpRequestBodyInit,
  headers: Record<string, string>,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<Sent> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url)

    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value)

    xhr.upload.onprogress = (e) => {
      /*
       * <b>A `total` of zero is not nothing uploaded, it is nothing known.</b>
       * Some mobile browsers cannot measure a large body, and dividing by it
       * gives `Infinity` or `NaN` — a bar that jumps to the end, or vanishes.
       * Reporting nothing leaves the caller showing a spinner, which is the
       * honest answer when the number is genuinely unavailable.
       */
      if (e.lengthComputable && e.total > 0) onProgress?.(e.loaded / e.total)
    }

    /*
     * ⚠ **The abort listener comes off when this request settles.**
     *
     * `{ once: true }` only removes it when it *fires*, so a request that
     * finishes normally leaves its listener on the signal for ever. One signal
     * now covers a whole chunked upload — sixty-odd blocks for a 254 MB video
     * — so cancelling one tile called `abort()` on every **already-finished**
     * XHR of that transfer. A browser treats that as a harmless no-op, which
     * is why it was invisible; the test suite does not, and four uncaught
     * `TypeError`s were the only thing saying so.
     *
     * `pause()` in the upload core already does exactly this, for exactly this
     * reason.
     */
    const stopThis = () => xhr.abort()
    const settled = () => signal?.removeEventListener('abort', stopThis)

    xhr.onload = () => {
      settled()
      onProgress?.(1)
      resolve({ status: xhr.status, text: xhr.responseText })
    }

    // Deliberately not the status: a network failure has none, and "0" on the
    // screen is worse than a sentence.
    xhr.onerror = () => {
      settled()
      reject(new Error('The upload stopped before it finished.'))
    }

    xhr.ontimeout = () => {
      settled()
      reject(new Error('The upload timed out.'))
    }

    xhr.onabort = () => {
      settled()
      reject(new Error('The upload was cancelled.'))
    }

    /*
     * **Already cancelled means never sent.** `addEventListener('abort')` does
     * not fire on a signal that has *already* aborted, so without this the
     * bytes go out and the request resolves 201 for something nobody wanted —
     * `observed` against the real source with a pre-aborted signal. A human
     * click cannot land in that window today, but anything that aborts
     * programmatically can, which is the next slice's encode deadline.
     */
    if (signal?.aborted) {
      reject(new Error('The upload was cancelled.'))
      return
    }

    signal?.addEventListener('abort', stopThis)

    xhr.send(body)
  })
}
